import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LocalStorageService } from './local-storage.service';
import { AuthService, RefreshTokenResponse } from './auth.service';

export const InterceptorSkipHeader = 'X-Skip-Interceptor';

type LangCode = 'pt' | 'en' | 'es';
const BACK_LANG_MAP: Record<LangCode, string> = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es-ES',
};

const GLOBAL_LANG_KEY = 'appLang';
const userLangKey = (userId: string) => `appLang_${userId}`;

function normalizeLang(input?: string | null): LangCode {
  const raw = (input ?? '').toLowerCase();
  if (raw.startsWith('pt')) return 'pt';
  if (raw.startsWith('en')) return 'en';
  if (raw.startsWith('es')) return 'es';
  return 'pt';
}

function getUserId(storage: LocalStorageService): string | null {
  const byKey = storage.get<string>('user_id');
  if (byKey) return byKey;

  const user =
    (storage.get('user') as { id?: string } | null) ||
    (storage.get('authUser') as { id?: string } | null) ||
    (storage.get('session') as { id?: string } | null);

  return user?.id ?? null;
}

function resolveHeaderLang(storage: LocalStorageService): string {
  const uid = getUserId(storage);
  const userKey = uid ? userLangKey(uid) : null;

  const userLangRaw = userKey ? (storage.get(userKey) as string | null) : null;
  const globalLangRaw = storage.get(GLOBAL_LANG_KEY) as string | null;
  const nav =
    (typeof navigator !== 'undefined' ? navigator.language : 'pt') || 'pt';

  const short = normalizeLang(userLangRaw ?? globalLangRaw ?? nav);
  return BACK_LANG_MAP[short];
}

function extractToken(res: RefreshTokenResponse): string | null {
  return (
    res?.token ??
    res?.accessToken ??
    res?.data?.token ??
    res?.data?.accessToken ??
    null
  );
}

function extractRefreshToken(res: RefreshTokenResponse): string | null {
  return (
    res?.refresh_token ??
    res?.refreshToken ??
    res?.data?.refresh_token ??
    res?.data?.refreshToken ??
    null
  );
}

@Injectable({ providedIn: 'root' })
export class AuthInterceptorService implements HttpInterceptor {
  private readonly isBrowser: boolean;
  private isRefreshing = false;
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly storage: LocalStorageService,
    private readonly router: Router,
    private readonly authService: AuthService,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const langHeader = resolveHeaderLang(this.storage);

    const hasSkip = req.headers.has(InterceptorSkipHeader);
    const url = req.url || '';
    const shouldSkipByUrl =
      url.includes('/sso/apple/callback') ||
      url.includes('/user/apple') ||
      url.includes('/sso/google/callback') ||
      url.includes('/user/google');

    const shouldSkipAuth = hasSkip || shouldSkipByUrl;

    const cleanHeaders = hasSkip
      ? req.headers.delete(InterceptorSkipHeader)
      : req.headers;

    const connectionId = this.storage.get<string>('connection') ?? '';
    const token = this.storage.get<string>('access_token');

    const headersToSet: Record<string, string> = {
      'X-Lang': langHeader,
      'Accept-Language': langHeader,
    };

    if (!shouldSkipAuth) {
      if (token) headersToSet['Authorization'] = `Bearer ${token}`;
      if (connectionId) headersToSet['connection-id'] = connectionId;
    }

    const request = req.clone({
      headers: cleanHeaders,
      setHeaders: headersToSet,
    });

    return next.handle(request).pipe(
      catchError((err: unknown) => {
        if (
          !shouldSkipAuth &&
          this.isBrowser &&
          err instanceof HttpErrorResponse &&
          err.status === 401
        ) {
          return this.handle401Error(request, next);
        }
        return throwError(() => err);
      })
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const refreshToken = this.storage.get<string>('refresh_token');
    if (!refreshToken) {
      this.clearSessionAndRedirect();
      return throwError(() => new Error('No refresh token available'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refresh().pipe(
        switchMap((res) => {
          const newToken = extractToken(res);
          if (!newToken) {
            this.isRefreshing = false;
            this.clearSessionAndRedirect();
            return throwError(() => new Error('No token in refresh response'));
          }

          const newRefresh = extractRefreshToken(res);

          this.storage.set('access_token', newToken);
          if (newRefresh) this.storage.set('refresh_token', newRefresh);

          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);

          const connectionId = this.storage.get<string>('connection') ?? '';

          return next.handle(
            request.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
                ...(connectionId ? { 'connection-id': connectionId } : {}),
              },
            })
          );
        }),
        catchError((e) => {
          this.isRefreshing = false;
          this.clearSessionAndRedirect();
          return throwError(() => e);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((t) => t !== null),
      take(1),
      switchMap((t) => {
        const connectionId = this.storage.get<string>('connection') ?? '';
        return next.handle(
          request.clone({
            setHeaders: {
              Authorization: `Bearer ${t!}`,
              ...(connectionId ? { 'connection-id': connectionId } : {}),
            },
          })
        );
      })
    );
  }

  private clearSessionAndRedirect(): void {
    this.storage.remove('access_token');
    this.storage.remove('refresh_token');
    this.storage.remove('user');
    this.storage.remove('authUser');
    this.storage.remove('session');
    this.router.navigate(['public/login']).then();
  }
}
